import * as Routes from '@/modules/routes';

export interface RailPage {
  href: string;
  label: string;
  // The page has sub-routes (the edit page's sections), so it is current under them too.
  matchNested?: boolean;
  // A secondary word for tab bars ("3 questions", "none yet"); the rail ignores it.
  meta?: string;
  // Non-GET actions (legacy "Create" links) go through rails-ujs; shells without it skip them.
  method?: 'post';
  // SPA router path when the page has an SPA counterpart; shells with a router link there instead.
  to?: string;
}

// The legacy sysadmin sub-nav (layouts/headers/_sysadmin_sub_nav) is the canonical source for
// every list below. Keep the two in step until the strip goes away.

export const programPages = (programId: number): RailPage[] => [
  {
    href: Routes.admin_program_lessons_path(programId),
    label: 'Table of Contents',
    to: `/content/programs/${programId}`,
  },
  { href: Routes.admin_program_program_resources_path(programId), label: 'Resources' },
  { href: Routes.admin_program_maps_path(programId), label: 'Maps' },
  { href: Routes.admin_program_common_cartridges_path(programId), label: 'Exports' },
  { href: Routes.admin_program_program_kit_materials_path(programId), label: 'Kit Materials' },
  { href: Routes.find_dup_images_admin_program_path(programId), label: 'Find Duplicate Images' },
  {
    href: Routes.edit_admin_program_path(programId),
    label: 'Edit Program',
    matchNested: true,
    to: `/content/programs/${programId}/edit`,
  },
];

// The legacy header's Lesson Guides tab: the guides of the program's first lesson. Pages that know
// that lesson (the lessons page) put it in their tab row after Table of Contents.
export const lessonGuidesPage = (lessonId: number, programId: number): RailPage => ({
  href: Routes.admin_lesson_slide_shows_path(lessonId, { program_id: programId }),
  label: 'Lesson Guides',
  to: `/content/programs/${programId}/lessons/${lessonId}/guides`,
});

// The Units page is where units are added, reordered, and deleted: the Units section's own action.
export const manageUnitsPage = (programId: number): RailPage => ({
  href: Routes.admin_program_units_path(programId),
  label: 'Manage',
  to: `/content/programs/${programId}/units`,
});

export const unitPages = (unitId: number, programId: number): RailPage[] => {
  const params = { program_id: programId };

  return [
    {
      href: Routes.edit_admin_unit_path(unitId, params),
      label: 'Edit Unit',
      matchNested: true,
      to: `/content/programs/${programId}/units/${unitId}/edit`,
    },
    { href: Routes.unit_index_admin_notebooks_path({ program_id: programId, unit_id: unitId }), label: 'Projects' },
    { href: Routes.admin_unit_unit_resources_path(unitId, params), label: 'Resources' },
    { href: Routes.admin_unit_unit_project_materials_path(unitId, params), label: 'Materials' },
    { href: Routes.admin_unit_sections_path(unitId, params), label: 'Sections' },
    {
      href: Routes.unit_index_admin_unit_slide_shows_path(unitId, params),
      label: 'Slideshows',
      to: `/content/programs/${programId}/units/${unitId}/guides`,
    },
  ];
};

// The tab strip first, then the More menu's Lesson group. Science programs have Science
// Background where the others have Procedures. Print Tools (behind enable_print_tools) is the SPA
// print tools page; the legacy sections page had it as an in-page tab, and the legacy context bar
// sends it to the standalone tool.
export const lessonPages = (
  lessonId: number, programId: number, programIsScience: boolean, showPrintTools = false,
): RailPage[] => {
  const params = { program_id: programId };

  return [
    {
      href: Routes.admin_lesson_sections_path(lessonId, params),
      label: 'Sections',
      to: `/content/programs/${programId}/lessons/${lessonId}/sections`,
    },
    ...(showPrintTools ? [{
      href: Routes.admin_print_tools_path(),
      label: 'Print Tools',
      to: `/content/programs/${programId}/lessons/${lessonId}/print-tools`,
    }] : []),
    { href: Routes.admin_games_path(lessonId, params), label: 'Games' },
    lessonGuidesPage(lessonId, programId),
    { href: Routes.staffer_lesson_materials_path(lessonId, params), label: 'Materials' },
    { href: Routes.staffer_lesson_assessments_path(lessonId, params), label: 'Assessments' },
    { href: Routes.admin_lesson_path(lessonId, params), label: 'Overview' },
    { href: Routes.admin_lesson_modules_path(lessonId, params), label: 'Modules' },
    {
      href: Routes.admin_lesson_differentiating_instructions_path(lessonId, params),
      label: 'Differentiating Instruction',
    },
    { href: Routes.admin_lesson_enhancing_learnings_path(lessonId, params), label: 'Enhancing Learning' },
    programIsScience
      ? { href: Routes.admin_lesson_science_backgrounds_path(lessonId, params), label: 'Science Background' }
      : { href: Routes.admin_lesson_procedures_path(lessonId, params), label: 'Procedures' },
    { href: Routes.edit_admin_lesson_path(lessonId, params), label: 'Edit Lesson' },
  ];
};

export const globalPages = (programId: number): RailPage[] => {
  const scoped = { program_id: programId };

  return [
    { href: Routes.admin_cliparts_path(scoped), label: 'Clipart' },
    { href: Routes.admin_image_vendors_path(scoped), label: 'Image Vendors' },
    { href: Routes.staffer_program_question_banks_path(programId), label: 'Question Bank' },
    { href: Routes.admin_safety_notes_path(scoped), label: 'Safety Notes' },
    { href: Routes.admin_materials_path(scoped), label: 'Materials' },
    { href: Routes.admin_images_path(scoped), label: 'Images' },
    { href: Routes.admin_videos_path(scoped), label: 'Videos' },
    { href: Routes.find_dup_images_admin_images_path(), label: 'Find All Duplicate Images' },
    { href: Routes.admin_standards_path(scoped), label: 'Standards' },
  ];
};

export const toolPages = (programId: number, showSearch: boolean): RailPage[] => [
  { href: Routes.admin_translations_path(), label: 'Translate Spanish By Model' },
  { href: Routes.admin_assessment_validator_path(), label: 'Assessment Validator' },
  { href: Routes.admin_cleanup_index_path(), label: 'Track 12 Days of Cleanup' },
  ...(showSearch ? [{ href: Routes.search_admin_program_path(programId), label: 'Search' }] : []),
];

export const isPathCurrent = (path: string, pathname: string, matchNested = false) => (
  pathname === path || (matchNested && pathname.startsWith(`${path}/`))
);

export const isCurrentPage = (page: RailPage) => (
  isPathCurrent(page.href.split('?')[0], window.location.pathname, page.matchNested)
);

interface SectionFacts {
  id: number;
  notebooks_count: number;
  reading_check_questions_count: number | null;
  reading_game_id: number | null;
  tutorial_id: number | null;
}

// The sysadmin sections table's columns, per section: Text, Tutorial, Reading Check, Notebook.
// A missing tutorial or reading check is offered as its Create action (a rails-ujs post).
export const sectionPages = (section: SectionFacts, lessonId: number, programId: number): RailPage[] => {
  const scoped = { lesson_id: lessonId, program_id: programId };
  const questions = section.reading_check_questions_count ?? 0;

  return [
    { href: Routes.edit_admin_section_path(section.id, scoped), label: 'Text' },
    section.tutorial_id
      ? { href: Routes.admin_section_activity_path(section.id, scoped), label: 'Tutorial' }
      : {
        href: Routes.admin_section_activity_path(section.id, scoped),
        label: 'Tutorial',
        meta: 'none yet, create',
        method: 'post',
      },
    section.reading_game_id
      ? {
        href: Routes.edit_admin_reading_game_path(section.reading_game_id, { ...scoped, section_id: section.id }),
        label: 'Reading Check',
        meta: `${questions} ${questions === 1 ? 'question' : 'questions'}`,
      }
      : {
        href: Routes.admin_section_reading_games_path(section.id, scoped),
        label: 'Reading Check',
        meta: 'none yet, create',
        method: 'post',
      },
    {
      href: Routes.admin_section_notebooks_path(section.id, scoped),
      label: 'Notebooks',
      meta: String(section.notebooks_count),
    },
  ];
};
